import { ThemeProvider } from '@emotion/react'
import { Box, Button, createTheme } from '@mui/material'
import { StyledEngineProvider } from '@mui/material/styles'
import { ELPLAN_BID_MATRIX_REFRESH_EVENT_QUERY_KEY, useElplanBidMatrix } from 'api/elplan/elplan.api'
import { isEqual } from 'helpers/global.helper/global.helper'
import { queryClient } from 'helpers/queryClient'
import i18n from 'i18next'
import { MaterialReactTable, MRT_ColumnDef } from 'material-react-table'
import React, { ReactElement, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { AutoSizer } from 'react-virtualized-auto-sizer'
import theme from 'styles/theme/theme'
import Icon from 'ui/atoms/Icon/Icon'
import NewButton from 'ui/atoms/NewButton/NewButton'
import InfoBanner from 'ui/components/InfoBanner/InfoBanner'
import { registerDefaultProps, registerUiConfigComponent } from 'ui/uiConfig/factory'
import Datetime from 'utils/datetime/datetime'
import styles from './ElplanBidMatrix.module.less'
import type { ElplanBidMatrixConfigProps, ElplanBidMatrixProps } from './ElplanBidMatrix.types'

const defaultProps: ElplanBidMatrixConfigProps = {
  description:
    "This UiConfig fetches the latest generated bid matrix data in a simple read-only table, based on the system's preferences. If that is not setup you might not see anything. You cannot use standard children_ids with this uiconfig.",
  electricity_price_scenario_id: '<electricity_price_scenario_id> (from the scenario in django admin)',
}
registerUiConfigComponent(`elplan_bid_matrix`, ElplanBidMatrix)
registerDefaultProps(`elplan_bid_matrix`, defaultProps)
function getAccessorKeyFromColumnWithoutDot(column: string, index: number): string {
  //special function to remove dots from the column name, as it is not supported in MaterialReactTable
  return `${column.split('.')[0]}_${index}`
}
async function exportBidMatrix(rows: (string | number)[][], columns: string[], date: ISODateTime): Promise<void> {
  //Need to pass down rows and columns as the format differs from what the material react table can handle
  const XLSX = await import(`xlsx`)
  const workbook = XLSX.utils.book_new()
  const excelColumns = columns.map((column, index) => {
    return { accessorKey: `${column}_${index}`, header: column }
  })
  const generateExcelRows = (): Record<string, number | string>[] => {
    const tempRows: Record<string, number | string>[] = []
    rows.forEach((row) => {
      const localRow: Record<string, number | string> = {}
      row.forEach((value, elementIndex) => {
        localRow[`${columns[elementIndex]}_${elementIndex}`] = value
      })
      tempRows.push(localRow)
    })
    return tempRows
  }
  const excelRows = generateExcelRows()
  const excelHeaders = excelColumns.map((column, index) => `${column.header}_${index}` || '')
  const worksheet = XLSX.utils.json_to_sheet(excelRows, { header: excelHeaders }) // To make sure it is sorted based on column sorting from endpoint
  XLSX.utils.book_append_sheet(workbook, worksheet, i18n.t('Bid matrix'))
  if (!worksheet['!ref']) {
    throw new Error("Worksheet reference ('!ref') is undefined.")
  }
  const range = XLSX.utils.decode_range(worksheet['!ref'])
  //removes _ from accessorKeys which make out the column ids
  for (let C = range.s.c; C <= range.e.c; ++C) {
    const address = XLSX.utils.encode_col(C) + '1'
    if (!worksheet[address]) continue
    worksheet[address].v = worksheet[address].v.split('_')[0]
  }
  XLSX.writeFile(workbook, `abs_bid_matrix_${date}.xlsx`)
}
// Helper function to format table data for clipboard
function formatTableDataForClipboard(
  rows: (string | number)[][],
  columns: string[],
  excludeFirstColumn?: boolean
): string {
  let headerRow = columns.join('\t')
  let dataRows = rows.map((row) => row.join('\t'))

  if (excludeFirstColumn) {
    headerRow = columns.slice(1).join('\t')
    dataRows = rows.map((row) => row.slice(1).join('\t'))
  }
  return [headerRow, ...dataRows].join('\n')
}
// Helper function to handle clipboard copy
function handleCopyToClipboard(rows: (string | number)[][], columns: string[], excludeFirstColumn?: boolean): void {
  const tableData = formatTableDataForClipboard(rows, columns, excludeFirstColumn)
  navigator.clipboard.writeText(tableData)
}

function useQueryHasNewData(queryRefreshKey: string, params: Record<string, any>): [boolean, () => void] {
  //returns a boolean if there is new data (if the exact query and params has been invalidated)
  const [hasNewData, setHasNewData] = useState(false)
  useEffect(() => {
    const queryKey = [queryRefreshKey, params]

    const unsubscribe = queryClient.getQueryCache().subscribe((event) => {
      if (isEqual(event.query.queryKey, queryKey) && event.type === 'updated') {
        if (event.query.state.isInvalidated) {
          setHasNewData(true)
        }
      }
    })
    return () => {
      unsubscribe()
    }
  })
  const hasRefetchedNewData = (): void => setHasNewData(false)

  return [hasNewData, hasRefetchedNewData]
}
export default function ElplanBidMatrix({
  datasetStartTime,
  electricity_price_scenario_id,
}: ElplanBidMatrixProps): ReactElement {
  //make sure that we know of the chosen date in the bid management view

  const chosenDate = useMemo(() => {
    return Datetime.toLocalTime(datasetStartTime, 'onlyDate')
  }, [datasetStartTime])
  const { t } = useTranslation()
  const tableTheme = useMemo(
    () =>
      createTheme({
        palette: {
          primary: theme.palette.primary,
          secondary: theme.palette.secondary,
          info: theme.palette.info,
        },
      }),
    []
  )
  const scenarioID = (electricity_price_scenario_id as number) || 0
  const params: Record<string, any> = {
    date: chosenDate as ISODateTime,
    electricity_price_scenario: scenarioID,
  }
  const queryRefreshKey = ELPLAN_BID_MATRIX_REFRESH_EVENT_QUERY_KEY
  const [hasNewData, hasRefetchedNewData] = useQueryHasNewData(queryRefreshKey, params)

  const {
    isLoading: loading,
    data: elplanBidMatrix,
    refetch,
  } = useElplanBidMatrix(
    params,
    queryRefreshKey,
    false // enabled: false
  )

  // ONLY refetch data on mount of component, all other data will be refetched by a button click below
  useEffect(() => {
    refetch()
    hasRefetchedNewData()
  }, [hasRefetchedNewData, refetch])

  // Sort the data numerically by column headers (except first column)
  const sortedBidMatrixData = useMemo(() => {
    if (!elplanBidMatrix) {
      return null
    }

    const columns = elplanBidMatrix.table_contents.columns
    const rows = elplanBidMatrix.table_contents.rows

    if (columns.length <= 1) {
      return elplanBidMatrix.table_contents
    }

    // Create array of column indices with their numeric values for sorting
    const columnIndices = columns.slice(1).map((column, index) => ({
      originalIndex: index + 1,
      numericValue: parseFloat(column),
      column,
    }))

    // Sort by numeric value
    columnIndices.sort((a, b) => {
      if (isNaN(a.numericValue) && isNaN(b.numericValue)) return 0
      if (isNaN(a.numericValue)) return 1
      if (isNaN(b.numericValue)) return -1
      return a.numericValue - b.numericValue
    })

    // Create new sorted columns array (first column + sorted remaining columns)
    const sortedColumns = [columns[0], ...columnIndices.map((item) => item.column)]

    // Create new sorted rows array
    const sortedRows = rows.map((row) => [
      row[0], // Keep first column data in place
      ...columnIndices.map((item) => row[item.originalIndex]), // Reorder remaining data
    ])

    return {
      columns: sortedColumns,
      rows: sortedRows,
    }
  }, [elplanBidMatrix])

  const tableColumns: MRT_ColumnDef<Record<string, number | string>>[] = useMemo(() => {
    if (!sortedBidMatrixData) return []
    return sortedBidMatrixData.columns.map((column: string, index: number) => {
      if (index === 0) {
        //price hour
        return { accessorKey: getAccessorKeyFromColumnWithoutDot(column, index), header: column, grow: true, size: 50 }
      }
      return { accessorKey: getAccessorKeyFromColumnWithoutDot(column, index), header: column, grow: false, size: 5 }
    })
  }, [sortedBidMatrixData])
  const tableRows: Record<string, number | string>[] = useMemo(() => {
    const rows: Record<string, number | string>[] = [] //rows in material react table format
    sortedBidMatrixData?.rows.forEach((row: (string | number)[]) => {
      const localRow: Record<string, number | string> = {}
      row.forEach((value, elementIndex) => {
        localRow[getAccessorKeyFromColumnWithoutDot(sortedBidMatrixData.columns[elementIndex], elementIndex)] = value //MaterialReactTable does not work with dots in the accessorKey
      })
      rows.push(localRow)
    })
    return rows
  }, [sortedBidMatrixData])

  return (
    <div className={styles.ElplanBidMatrix_container}>
      <AutoSizer
        renderProp={({ width }) => (
          <div style={{ width: width ?? 0, overflowX: `auto`, overflowY: 'hidden' }}>
            <StyledEngineProvider injectFirst>
              <ThemeProvider theme={tableTheme}>
                <MaterialReactTable
                  key={'key'}
                  enableTopToolbar={true}
                  enableStickyHeader={true}
                  enableStickyFooter={false}
                  enableEditing={false}
                  columns={tableColumns}
                  data={tableRows}
                  enableColumnActions={false}
                  enableColumnFilters={false}
                  enableColumnResizing
                  enableSorting={false}
                  enableColumnOrdering={false}
                  manualFiltering={true}
                  manualPagination={false}
                  layoutMode="semantic"
                  initialState={{
                    pagination: { pageIndex: 0, pageSize: 25 },
                  }}
                  renderTopToolbarCustomActions={() => {
                    return (
                      <>
                        <div className={styles.ElplanBidMatrix_title}>
                          {t('Bid matrix for {{date}}', { date: chosenDate })}
                        </div>
                        {hasNewData && (
                          <>
                            <NewButton
                              label={t('Fetch latest bid matrix data')}
                              onClick={async () => {
                                await refetch()
                                hasRefetchedNewData()
                              }}
                            />
                            <InfoBanner
                              style="warning"
                              text={t('Updated bid matrix data is available for this date')}
                            />{' '}
                          </>
                        )}
                      </>
                    )
                  }}
                  renderToolbarInternalActions={() => (
                    <Box>
                      <Button
                        disabled={!sortedBidMatrixData}
                        onClick={() => {
                          const rows = sortedBidMatrixData?.rows || []
                          const columns = sortedBidMatrixData?.columns || []
                          handleCopyToClipboard(rows, columns, true)
                        }}
                        startIcon={<i className="fal fa-copy" />}
                      >
                        {t('Copy to clipboard (exclude first column)')}
                      </Button>
                      <Button
                        disabled={!sortedBidMatrixData}
                        onClick={() => {
                          const rows = sortedBidMatrixData?.rows || []
                          const columns = sortedBidMatrixData?.columns || []
                          handleCopyToClipboard(rows, columns)
                        }}
                        startIcon={<i className="fal fa-copy" />}
                      >
                        {t('Copy to clipboard')}
                      </Button>
                      <Button
                        disabled={!sortedBidMatrixData || !chosenDate}
                        onClick={() => {
                          if (sortedBidMatrixData && chosenDate) {
                            exportBidMatrix(
                              sortedBidMatrixData.rows || [],
                              sortedBidMatrixData.columns || [],
                              chosenDate as ISODateTime
                            )
                          }
                        }}
                        startIcon={<i className="fal fa-download" />}
                      >
                        {t('Export bid matrix')}
                      </Button>
                    </Box>
                  )}
                  renderEmptyRowsFallback={() => {
                    if (loading) {
                      return (
                        <div className={styles.ElplanBidMatrix_tableContent}>
                          <Icon icon="far fa-spinner-third fa-spin" color="gray" large />
                          {t('Loading...')}
                        </div>
                      )
                    } else {
                      return (
                        <div className={styles.ElplanBidMatrix_tableContent}>
                          {t('No bid matrix available for chosen date. Please choose another date.')}
                        </div>
                      )
                    }
                  }}
                  muiTableBodyRowProps={({ staticRowIndex }) => {
                    return {
                      sx: {
                        backgroundColor: staticRowIndex % 2 === 0 ? 'transparent' : theme.palette.background.default,
                      },
                    }
                  }}
                  muiTableFooterCellProps={({ column }: any) => {
                    return {
                      align: column.columnDef.align,
                      sx: {
                        fontFamily: 'monospace, monospace',
                        fontSize: '110%',
                        fontWeight: '600',
                        paddingRight: '8px',
                      },
                    }
                  }}
                />
              </ThemeProvider>
            </StyledEngineProvider>
          </div>
        )}
      />
    </div>
  )
}
